import React from 'react';
import { Gift, XCircle, ShieldCheck } from 'lucide-react';
import { track } from '../../utils/analytics';
import { APP_VERSION } from '../../constants/appVersion';

const HERO_COLLAGE = {
  tall:    'https://plus.unsplash.com/premium_photo-1683120883457-6ee9490a68f5?auto=format&fit=crop&w=900&q=70&fm=webp',
  topRight:'https://images.unsplash.com/photo-1577221084712-45b0445d2b00?auto=format&fit=crop&w=700&q=70&fm=webp',
  botRight:'https://images.unsplash.com/photo-1641337221253-fdc7237f6b61?auto=format&fit=crop&w=700&q=70&fm=webp',
};

interface HeroSectionProps {
  onOpenAuth: (mode: 'login' | 'register') => void;
}

export function HeroSection({ onOpenAuth }: HeroSectionProps) {
  return (
    <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 px-6 sm:px-8 overflow-hidden min-h-screen flex flex-col items-center justify-center">
      {/* Hero gradient background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(202,253,0,0.08),transparent_40%),radial-gradient(circle_at_bottom_left,rgba(0,241,254,0.08),transparent_40%)]" />

      {/* Bodybuilder backdrop is now mounted at the page level via
          <StaticBodyBackdrops />, so the imagery stays fixed in the viewport
          and remains visible behind every section as the user scrolls. */}

      <div className="relative max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* Left Content */}
        <div className="lg:col-span-7 space-y-6 sm:space-y-8 text-center lg:text-left">
          {/* Version Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#20262f] border border-[#44484f]/20">
            <span className="w-2 h-2 rounded-full bg-[#cafd00] animate-pulse" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#a8abb3]">Version {APP_VERSION} Live</span>
          </div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-headline font-bold tracking-tighter leading-[0.95] text-[#f1f3fc]">
            Build Your <span className="text-[#cafd00]">Dream Body.</span>
            <span className="block mt-3 text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#f1f3fc]/90">
              Use our app<span className="sr-only"> AI Coach</span> for <span className="text-[#00f1fe]">Gym &amp; Nutrition</span>
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-[#a8abb3] max-w-xl leading-relaxed lg:mx-0 mx-auto">
            <strong className="text-[#f1f3fc]">All-in-one app to achieve your dream with premade or AI made programs</strong>.
            {/* SEO-only long form: invisible to users, crawlable by search engines. */}
            <span className="sr-only">AI workout plans with APRE-10 autoregulation and DUP-3 daily undulating periodization. Menu photo calorie counter, plate scanner, fridge scan. Complete gym tracker with superset support and an AI Analyzer that audits your progress every two weeks. Gym finder for traveling. Don&rsquo;t waste money on multiple apps to track your progress.</span>
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-2 lg:justify-start justify-center items-center">
            <button
              onClick={() => { track('landing_cta_clicked', { location: 'hero' }); onOpenAuth('register'); }}
              className="bg-[#cafd00] text-[#3a4a00] h-14 px-10 rounded-full font-bold text-lg flex items-center justify-center gap-3 hover:scale-95 transition-transform shadow-[0_0_20px_rgba(202,253,0,0.15)] font-headline"
              data-testid="hero-get-started-btn"
            >
              Start Reshaping
            </button>
            <button
              onClick={() => { track('landing_login_clicked', { location: 'hero' }); onOpenAuth('login'); }}
              className="h-14 px-8 rounded-full font-bold text-lg text-[#f1f3fc] border border-[#44484f]/30 hover:border-[#cafd00]/50 hover:text-[#cafd00] transition-all font-headline"
              data-testid="hero-login-btn"
            >
              Log In
            </button>
          </div>

          <ul className="flex flex-wrap gap-x-6 gap-y-3 text-[#f1f3fc] text-base sm:text-lg lg:justify-start justify-center pt-1">
            <li className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-[#cafd00]" />
              <span className="font-semibold">2 weeks free</span>
            </li>
            <li className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-[#00f1fe]" />
              <span className="font-semibold">Cancel anytime</span>
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#cafd00]" />
              <span className="font-semibold">30-days money back guarantee</span>
            </li>
          </ul>
          <p className="text-base sm:text-lg text-[#a8abb3] italic lg:text-left text-center max-w-xl lg:mx-0 mx-auto -mt-1">
            If you&rsquo;re not totally in love with the product, we will refund you 100%.
          </p>
        </div>

        {/* Right - 3-photo athletic collage (replaces dashboard screenshot on tablet/desktop).
            Lighthouse audit (May 27, 2026) flagged Cumulative Layout Shift = 0.451,
            almost entirely from these images. The fix is two-pronged:
              1. Give the outer grid an explicit `aspect-ratio` so rows have a
                 reserved height BEFORE images decode (otherwise grid-rows-2
                 auto-sizes to natural image height and reflows on load).
              2. Add explicit `width`/`height` attributes per `<img>` so the
                 browser can reserve intrinsic-size space immediately, even
                 before CSS computes the cell dimensions.
            The values below mirror the rendered aspect of each cell at the
            collage's `max-w-[560px]` baseline (tall ≈ 3:4, smaller ≈ 7:4). */}
        <div className="hidden md:flex lg:col-span-5 relative justify-center" data-testid="hero-collage">
          <div className="relative z-10 w-full max-w-[560px] grid grid-cols-5 grid-rows-2 gap-3 aspect-[5/6] transform lg:rotate-1">
            {/* Tall left — F3 */}
            <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden border border-[#44484f]/25 shadow-2xl shadow-black/40 bg-[#151a21]">
              <img
                alt="Fitness&Me: Reshape Your Body"
                src={HERO_COLLAGE.tall}
                width={336}
                height={448}
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>
            {/* Top right — M1 */}
            <div className="col-span-2 row-span-1 rounded-2xl overflow-hidden border border-[#44484f]/25 shadow-xl shadow-black/40 bg-[#151a21]">
              <img
                alt="Fitness&Me: Build Your Dream Body"
                src={HERO_COLLAGE.topRight}
                width={224}
                height={220}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            {/* Bottom right — M3 */}
            <div className="col-span-2 row-span-1 rounded-2xl overflow-hidden border border-[#44484f]/25 shadow-xl shadow-black/40 bg-[#151a21]">
              <img
                alt="Fitness&Me All-in-One Fitness App"
                src={HERO_COLLAGE.botRight}
                width={224}
                height={220}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
          {/* Decorative blurs */}
          <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#cafd00]/10 blur-[100px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#00f1fe]/10 blur-[120px] rounded-full pointer-events-none" />
        </div>
      </div>
    </section>
  );
}
